// Single source of truth for the unified 3-plan billing model.
//
// One subscription grants a monthly pool of CREDITS. There is no separate
// "actions" concept — both Build and Grow spend credits (1 credit = 1 unit of
// work) from the same balance, under the same account-wide billing rules. Grow
// has no allowance, cap or limit of its own (migration 216, PANDA-2524).
//
// buildCreditsPerMonth here MUST match the DB-side _plan_build_grant in
// supabase/migrations/104_unified_credits.sql. Drift = silent grant mismatch.

import {
  tierMonthlyCents,
  tierAnnualCents,
  PLAN_BASE_CREDITS,
  type PaidTierId,
} from "@/lib/pricing/credit-tiers";

// Flat credit cost of one standard action (a build, a chat edit, a grow
// draft, etc.). Credits are denominated 10× finer than the old 1-credit-per-
// action model, so one action now costs 10 credits — i.e. "10 credits = 1
// action", the rate surfaced to users. Plan grants and top-up packs are scaled
// ×10 to match, so the action-economics are unchanged (Pro = 2000 cr = 200
// actions). Media/domain spend stays usage-priced (see lib/credits/*-pricing).
export const CREDITS_PER_ACTION = 10;

// Free-tier daily habit loop (migration 131). New free users get
// FREE_PLAN_TOTAL_CREDITS upfront, but may only SPEND FREE_DAILY_CAP per day
// (unspent credits bank/carry forward). After FREE_WINDOW_DAYS the first-month
// window ends and any remaining free credits are forfeited. Mirrors the DB:
// _plan_build_grant('free') and the free_daily_* columns on user_billing.
export const FREE_DAILY_CAP = 50;
export const FREE_WINDOW_DAYS = 30;
export const FREE_PLAN_TOTAL_CREDITS = 300;
// Legacy UI fallback only — NO LONGER the enforcement threshold. Enforcement
// now gates per-action: a build may start only when today's remaining room
// covers that action's full hold (migration 134, reserve_credits: room >=
// p_cost), which guarantees free_daily_used can never exceed the cap. This
// constant survives purely as a last-ditch fallback for the daily-cap modal's
// "needs at least N" line when the server doesn't send daily_min_required.
export const FREE_DAILY_MIN_ROOM = 10;

// One-time bonus grants. These are the SINGLE source for both the grant code
// and every piece of user-facing copy that quotes the amount — never inline
// the number. The ×10 re-denomination (CREDITS_PER_ACTION above) scaled the
// grants but missed several copy sites and the reconcile self-heal path, so
// the modal promised 5 while the webhook granted 50. Related: PANDA-1349,
// PANDA-1359 (pricing/credits copy standardization).
//
// CARD_ON_FILE_BONUS_CREDITS: granted once when a user saves a card (no
// charge). Mirrored by applyCardBonus and /api/stripe/reconcile-card.
export const CARD_ON_FILE_BONUS_CREDITS = 50;
// EMAIL_VERIFY_BONUS_CREDITS: granted once when an email/password user clicks
// the magic link and /auth/confirm flips email_verified_at for the first time
// (GT-15). Social-login users never reach that route — their provider already
// verified the address, and migration 075 exempts them from the gate — so they
// are outside this reward by construction. See maybeApplyEmailVerifyBonus.
export const EMAIL_VERIFY_BONUS_CREDITS = 10;
// EARLY_UPGRADE_BONUS_CREDITS: granted once when a free user upgrades while
// they still have free credits left. See maybeApplyEarlyUpgradeBonus.
export const EARLY_UPGRADE_BONUS_CREDITS = 500;

// Tier ladder: Free → Pro → Grow → Scale (renamed from Free → Starter → Pro →
// Scale; see migration 138). "starter" is retained ONLY as a tolerated legacy
// value — no live row or new signup uses it post-cutover (its old Stripe
// price-ids alias to "pro" in planFromPriceId). New paid tiers price
// dynamically via lib/pricing/credit-tiers.ts (credits × rate × multiplier).
export type PlanId = "free" | "starter" | "pro" | "grow" | "scale";
export type PaidPlanId = Exclude<PlanId, "free">;
export type Cadence = "monthly" | "annual";

// ── Grow Mode and credits ──────────────────────────────────────────────────
//
// Grow has NO allowance of its own. It spends the shared credit pool under the
// same account-wide billing rules as Build (migration 216, PANDA-2524), so
// there is no per-plan Grow number here to keep in step with SQL.
//
// What used to live here was GROW_DAILY_ACTIONS / growDailyCreditCap(): a
// second, Grow-only daily allowance stacked on top of the shared balance. It
// was decided in two places and drifted twice (PANDA-2448, PANDA-2449), and
// even correct it left an owner unable to say what they could spend — and gave
// an account with credits in the bank a refusal that buying credits did not
// fix. Raising it (PANDA-2523) and letting owners set it (PANDA-2483) are both
// superseded by removing it. Do not reintroduce a Grow-specific cap: a Grow
// action is affordable exactly when the shared balance covers it.

/**
 * Whether a plan actually includes autonomous Grow Mode. Grow Mode is a Grow-
 * and Scale-tier feature (migration 138): Free and the build-only "pro" tier do
 * NOT include it, so those users must not see Grow surfaces. The legacy
 * "starter" alias mirrors Pro
 * → no Grow. Single source of truth for gating Grow entitlement so the rule
 * can't drift into scattered plan-name comparisons; an unknown/nullish plan is
 * treated as no access. Mirrors the paid-tier gate in lib/growth/visibility.ts.
 */
export function hasGrowModeAccess(
  plan: PlanId | string | null | undefined,
): boolean {
  return plan === "grow" || plan === "scale";
}

export interface PlanDef {
  id: PlanId;
  name: string;
  tagline: string;
  buildCreditsPerMonth: number;
  projectsLimit: number;
  // Max concurrent live E2B preview VMs the user may run at once. Distinct
  // from projectsLimit (how many projects can EXIST): this caps how many can
  // be previewing simultaneously, since each live VM is a billable cloud
  // machine. Enforced in lib/sandbox/e2b-server.ts (oldest evicted first).
  concurrentSandboxes: number;
  customDomain: boolean;
  acceptPayments: boolean;
  removeBranding: boolean;
  // Advanced-completion capabilities gated app-type-agnostically (PANDA-1695).
  // Free users get a demo-safe build (labeled placeholders) instead; only a paid
  // plan turns these into real, published capabilities. See lib/features/
  // paid-features.ts, which is the single classifier that reads these flags.
  //   customerAccounts — real login / signup / account area / customer records
  //   persistentData   — saved bookings / submissions / records (real DB writes)
  //   notifications    — email/SMS reminders & confirmations (still needs setup)
  customerAccounts: boolean;
  persistentData: boolean;
  notifications: boolean;
  // Routes this plan's builds and repairs through the stronger premium build
  // model (MODELS.premium — Opus 4.8) instead of the standard model (Sonnet 5).
  // The single source of truth for premium model routing: lib/ai/model-quality-
  // tier.ts reads this flag, and the "stronger build model" upgrade copy is
  // driven by the same field so what we route and what we advertise cannot drift.
  // Premium build turns settle at a discounted credit multiplier
  // (lib/credits/value.ts) so a paid user's credits-per-build stays roughly flat.
  premiumBuildModel: boolean;
  prioritySupport: boolean;
  monthly: { priceUsd: number; stripePriceIdEnv: string | null };
  annual: { priceUsd: number; stripePriceIdEnv: string | null };
  highlighted: boolean;
}

// Paid-tier base prices are DERIVED from the authoritative pricing catalog
// (lib/pricing/credit-tiers.ts) — never hardcoded — so the plan-card / "from $X"
// display can never drift from what dynamic checkout + Stripe actually charge.
// Each paid tier's `priceUsd` is the price of that tier's OWN base credit
// allowance (Pro 300 → $29, Grow 1,000 → $97, Scale 3,000 → $291); the real
// recurring charge for a chosen credit total is computed per-selection by
// credit-tiers. Annual = ×10 (two months free) → $290 / $970 / $2,910.
//
// PANDA-1414: these MUST pass each tier's own PLAN_BASE_CREDITS. They used to
// pass one shared DEFAULT_LADDER_CREDITS (650) for every tier and let the ×2/×4
// multiplier separate the prices. Now that the rate is flat, feeding all three
// tiers the same credit count would price them IDENTICALLY.
const baseMonthlyUsd = (tier: PaidTierId): number =>
  tierMonthlyCents(tier, PLAN_BASE_CREDITS[tier]) / 100;
const baseAnnualUsd = (tier: PaidTierId): number =>
  tierAnnualCents(tier, PLAN_BASE_CREDITS[tier]) / 100;

export const PLANS: Record<PlanId, PlanDef> = {
  free: {
    id: "free",
    name: "Free",
    tagline: "Try Panda",
    // 300 credits granted upfront, but spend is capped at FREE_DAILY_CAP (50)
    // per day for the first FREE_WINDOW_DAYS (30). See migration 131.
    buildCreditsPerMonth: 300,
    projectsLimit: 3,
    concurrentSandboxes: 3,
    customDomain: false,
    acceptPayments: false,
    removeBranding: false,
    customerAccounts: false,
    persistentData: false,
    notifications: false,
    premiumBuildModel: false,
    prioritySupport: false,
    monthly: { priceUsd: 0, stripePriceIdEnv: null },
    annual: { priceUsd: 0, stripePriceIdEnv: null },
    highlighted: false,
  },
  // LEGACY (migration 138): no new signups, no live rows. Kept so PLANS["starter"]
  // never throws for any stale reference. Its old Stripe price-ids now resolve to
  // "pro" via planFromPriceId. Values mirror the new "pro" tier.
  starter: {
    id: "starter",
    name: "Pro",
    tagline: "Build & launch under your own brand",
    // Mirrors the live "pro" tier (PANDA-1414: 650 → 300).
    buildCreditsPerMonth: 300,
    projectsLimit: 1000,
    concurrentSandboxes: 10,
    customDomain: true,
    acceptPayments: true,
    removeBranding: true,
    customerAccounts: true,
    persistentData: true,
    notifications: true,
    premiumBuildModel: true,
    prioritySupport: false,
    monthly: { priceUsd: baseMonthlyUsd("pro"), stripePriceIdEnv: null },
    annual: { priceUsd: baseAnnualUsd("pro"), stripePriceIdEnv: null },
    highlighted: false,
  },
  // PRO — build & launch tier ("Most popular"). Was "starter". Credit total +
  // recurring price come from the credit dropdown (lib/pricing/credit-tiers.ts);
  // buildCreditsPerMonth is only the fallback grant for legacy flat rows that
  // have no custom_monthly_credits. priceUsd is the base (650-credit) starting
  // price for "from $X" copy. No fixed Stripe price (dynamic checkout).
  pro: {
    id: "pro",
    name: "Pro",
    tagline: "Build & launch under your own brand.",
    // PANDA-1414: 650 → 300 (the tier's new included allowance, $29/mo).
    // This is ALSO the renewal-grant fallback for rows with a NULL
    // custom_monthly_credits, so migration 139 backfills those rows to their
    // pre-cutover grant first — otherwise a grandfathered subscriber would
    // silently renew with fewer credits at their old price.
    buildCreditsPerMonth: 300,
    projectsLimit: 1000,
    concurrentSandboxes: 10,
    customDomain: true,
    acceptPayments: true,
    removeBranding: true,
    customerAccounts: true,
    persistentData: true,
    notifications: true,
    premiumBuildModel: true,
    prioritySupport: false,
    monthly: { priceUsd: baseMonthlyUsd("pro"), stripePriceIdEnv: null },
    annual: { priceUsd: baseAnnualUsd("pro"), stripePriceIdEnv: null },
    highlighted: true,
  },
  // GROW — marketing tier (×2). Was "pro". Adds the autonomous Grow Mode
  // marketing tools on top of Pro. buildCreditsPerMonth is the legacy fallback
  // (old pro = 2000) for grandfathered rows; new rows use custom_monthly_credits.
  grow: {
    id: "grow",
    name: "Grow",
    tagline: "Get customers on autopilot.",
    // PANDA-1414: 2000 → 1000 ($97/mo). See the pro note re: migration 139.
    buildCreditsPerMonth: 1000,
    projectsLimit: 1000,
    concurrentSandboxes: 15,
    customDomain: true,
    acceptPayments: true,
    removeBranding: true,
    customerAccounts: true,
    persistentData: true,
    notifications: true,
    premiumBuildModel: true,
    prioritySupport: false,
    monthly: { priceUsd: baseMonthlyUsd("grow"), stripePriceIdEnv: null },
    annual: { priceUsd: baseAnnualUsd("grow"), stripePriceIdEnv: null },
    highlighted: false,
  },
  // SCALE — team tier (×4). Unchanged name; now credit-priced like the others.
  scale: {
    id: "scale",
    name: "Scale",
    tagline: "Run a team of AI businesses.",
    // PANDA-1414: 4000 → 3000 ($291/mo). See the pro note re: migration 139.
    buildCreditsPerMonth: 3000,
    projectsLimit: 1000,
    concurrentSandboxes: 20,
    customDomain: true,
    acceptPayments: true,
    removeBranding: true,
    customerAccounts: true,
    persistentData: true,
    notifications: true,
    premiumBuildModel: true,
    prioritySupport: true,
    monthly: { priceUsd: baseMonthlyUsd("scale"), stripePriceIdEnv: null },
    annual: { priceUsd: baseAnnualUsd("scale"), stripePriceIdEnv: null },
    highlighted: false,
  },
};

// Paid tiers offered to NEW customers, in display order. "starter" is excluded
// (legacy only). Used for checkout validation, price lookups, and webhook
// resolution iteration.
export const PAID_PLAN_IDS: readonly PaidPlanId[] = ["pro", "grow", "scale"];

/**
 * Canonical "is this a paid plan" check (PANDA-1342). One-off top-ups and auto
 * top-up are paid-only, so every credit surface derives eligibility from this
 * single helper instead of scattering `plan !== "free"` string checks that can
 * drift. Any known plan other than "free" is paid (including the legacy
 * "starter"); an unknown / empty / nullish value is treated as NOT paid so a
 * loading or malformed plan can never unlock a paid-only top-up path.
 */
export function isPaidPlan(plan: PlanId | string | null | undefined): boolean {
  return typeof plan === "string" && plan !== "free" && plan in PLANS;
}

// Canonical tier ranking for the unified ladder Free → Pro → Grow → Scale
// (migration 138). "starter" is the legacy Pro tier, kept one slot below the
// live "pro" so a grandfathered starter row still resolves as a real paid tier
// that upgrades INTO the live ladder. This is the single source of truth for
// every upgrade/downgrade comparison and "which tier is next" decision, so the
// upgrade modals (desktop + mobile) and the plan-gating helpers can never drift
// on tier order the way their old hard-coded, grow-less maps did.
export const PLAN_RANK: Record<PlanId, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  grow: 3,
  scale: 4,
};

/**
 * Rank of any plan value. An unknown / empty / nullish plan sorts as free (0) —
 * the lowest tier — so a loading or malformed plan is treated as "can still
 * upgrade" rather than accidentally hiding the upgrade path.
 */
export function planRank(plan: PlanId | string | null | undefined): number {
  return typeof plan === "string" && plan in PLAN_RANK
    ? PLAN_RANK[plan as PlanId]
    : 0;
}

/**
 * True when a higher paid tier exists above the given plan. Scale is the top of
 * the ladder, so it is the only plan with nothing to upgrade to — every other
 * plan (Free, Pro, Grow, and the legacy Starter) has a higher tier. Drives
 * whether an "Upgrade plan" entry point should appear at all.
 */
export function hasHigherPlan(
  plan: PlanId | string | null | undefined,
): boolean {
  return planRank(plan) < PLAN_RANK.scale;
}

/**
 * The immediate next paid tier up the ladder from the given plan, or null when
 * the plan is already at the top (Scale). Offers are always drawn from the live
 * PAID_PLAN_IDS (Pro → Grow → Scale), so the legacy Starter tier is never
 * offered as a destination. Used to pre-select the correct next-plan offer when
 * an existing subscriber opens the upgrade modal (e.g. Pro → Grow, Grow →
 * Scale) instead of the old grow-less fallback that offered Grow users a Pro
 * downgrade.
 */
export function nextPlanId(
  plan: PlanId | string | null | undefined,
): PaidPlanId | null {
  const rank = planRank(plan);
  for (const id of PAID_PLAN_IDS) {
    if (PLAN_RANK[id] > rank) return id;
  }
  return null;
}

/**
 * The live paid tiers strictly BELOW the given plan, ordered nearest-first
 * (highest of the lower tiers first) so a downgrade picker lists the closest
 * step down at the top — Scale → [Grow, Pro], Grow → [Pro], Pro → []. Drawn from
 * the live PAID_PLAN_IDS only, so the legacy "starter" tier is never offered as
 * a downgrade destination (a grandfathered starter row ranks below pro, so it
 * too returns []). This is the single source of truth for "which lower PAID
 * plans can this subscriber move to" — the downgrade flow must offer lower paid
 * tiers, never treat downgrade as a cancel-to-Free (PANDA-1411).
 */
export function lowerPaidPlanIds(
  plan: PlanId | string | null | undefined,
): PaidPlanId[] {
  const rank = planRank(plan);
  return PAID_PLAN_IDS.filter((id) => PLAN_RANK[id] < rank).sort(
    (a, b) => PLAN_RANK[b] - PLAN_RANK[a],
  );
}

/**
 * True when at least one live paid tier sits below the given plan — i.e. the
 * subscriber has a real "downgrade to a cheaper paid plan" option distinct from
 * cancelling to Free. False for Pro (the lowest paid tier), the legacy Starter,
 * Free, and unknown plans, whose only downgrade-equivalent action is
 * cancellation. Drives whether a "Downgrade plan" entry point is shown at all.
 */
export function hasLowerPaidPlan(
  plan: PlanId | string | null | undefined,
): boolean {
  return lowerPaidPlanIds(plan).length > 0;
}

/**
 * The lowest paid-plan price for a billing cadence, expressed PER MONTH exactly
 * as the plan cards present it:
 *   • monthly → the plan's monthly price (e.g. 29)
 *   • annual  → the annual price divided by 12 (e.g. 290/12 = 24.17), matching
 *     the "$X/month, billed annually" framing on the cards.
 *
 * Only counts plans that have BOTH a positive price and a configured Stripe
 * price-id env (i.e. are actually purchasable). Returns null when no paid plan
 * qualifies — callers should then fall back to non-price copy rather than
 * inventing a number. This is the single source of truth for any "upgrade
 * from $X" copy, so it can never drift from the cards.
 */
export function lowestPaidPlanMonthlyUsd(cadence: Cadence): number | null {
  let lowest: number | null = null;
  for (const id of PAID_PLAN_IDS) {
    const def = PLANS[id];
    const tier = cadence === "annual" ? def.annual : def.monthly;
    // Paid tiers price dynamically (no fixed Stripe env), so count any tier with
    // a positive base price — priceUsd is the base (650-credit) starting price.
    if (!tier.priceUsd || tier.priceUsd <= 0) continue;
    const perMonth = cadence === "annual" ? tier.priceUsd / 12 : tier.priceUsd;
    if (lowest === null || perMonth < lowest) lowest = perMonth;
  }
  return lowest;
}

/**
 * "$29/month" (monthly) or "$24.17/month" (annual, per-month equivalent) for
 * the cheapest purchasable plan, or null when pricing can't be resolved. Decimal
 * formatting mirrors the plan cards: whole dollars for monthly, two decimals for
 * the annual per-month figure.
 */
export function lowestPaidPlanPriceLabel(cadence: Cadence): string | null {
  const perMonth = lowestPaidPlanMonthlyUsd(cadence);
  if (perMonth === null) return null;
  const amount = cadence === "annual" ? perMonth.toFixed(2) : String(perMonth);
  return `$${amount}/month`;
}

// ── Stripe price ID lookup ────────────────────────────────────────────

export interface PriceIdMatch {
  plan: PaidPlanId;
  cadence: Cadence;
}

// New paid tiers price DYNAMICALLY (an inline Stripe price created per
// checkout), so their plan is carried in checkout/subscription metadata and
// fulfilled by the rated/tiered path — NOT resolved here. This function exists
// only to resolve LEGACY fixed-price subscriptions (customers who subscribed
// before migration 138) to their renamed key, so renewals + plan-change events
// keep working. The rename mapping: old Starter price → "pro", old Pro price →
// "grow", Scale → "scale". Returns null for any dynamic/unknown price (the
// caller treats null as "not a legacy fixed-price event" and skips).
function legacyPriceAliases(): Array<[string | undefined, PaidPlanId, Cadence]> {
  return [
    [process.env.STRIPE_PRICE_STARTER_MONTHLY, "pro", "monthly"],
    [process.env.STRIPE_PRICE_STARTER_ANNUAL, "pro", "annual"],
    [process.env.STRIPE_PRICE_PRO_MONTHLY, "grow", "monthly"],
    [process.env.STRIPE_PRICE_PRO_ANNUAL, "grow", "annual"],
    [process.env.STRIPE_PRICE_SCALE_MONTHLY, "scale", "monthly"],
    [process.env.STRIPE_PRICE_SCALE_ANNUAL, "scale", "annual"],
  ];
}

export function planFromPriceId(priceId: string | null | undefined): PriceIdMatch | null {
  if (!priceId) return null;
  for (const [env, plan, cadence] of legacyPriceAliases()) {
    if (env && env === priceId) return { plan, cadence };
  }
  return null;
}

// Webhook entry point. Returns the matched plan or null for any
// unknown / free / malformed line items — caller MUST treat null as a
// hard failure and log+skip rather than fall through to a default grant.
export function deriveSubscriptionGrant(
  lineItems: Array<{ price?: { id?: string | null } | null }>,
): (PriceIdMatch & { buildCreditsPerMonth: number }) | null {
  if (!Array.isArray(lineItems) || lineItems.length === 0) return null;
  for (const item of lineItems) {
    const match = planFromPriceId(item?.price?.id ?? null);
    if (match) {
      const def = PLANS[match.plan];
      return {
        ...match,
        buildCreditsPerMonth: def.buildCreditsPerMonth,
      };
    }
  }
  return null;
}

// ── Top-up packs ──────────────────────────────────────────────────────
// Top-ups add credits to the shared pool (used by both build and grow).

export type TopupPackId = "pack_100" | "pack_250" | "pack_500" | "pack_1000";

export interface TopupPack {
  id: TopupPackId;
  credits: number;
  priceCents: number;
  priceIdEnv: string;
}

// Dollar prices are unchanged from the pre-usage-based model; credits are ×10
// (1 credit is now ~$0.036 instead of ~$0.40). The pack ids/env keys keep
// their historical names even though each pack now grants 10× the credits.
export const TOPUP_PACKS: Record<TopupPackId, TopupPack> = {
  pack_100:  { id: "pack_100",  credits: 1000,  priceCents: 1500, priceIdEnv: "STRIPE_PRICE_PACK_100"  },
  pack_250:  { id: "pack_250",  credits: 2500,  priceCents: 2900, priceIdEnv: "STRIPE_PRICE_PACK_250"  },
  pack_500:  { id: "pack_500",  credits: 5000,  priceCents: 4900, priceIdEnv: "STRIPE_PRICE_PACK_500"  },
  pack_1000: { id: "pack_1000", credits: 10000, priceCents: 9900, priceIdEnv: "STRIPE_PRICE_PACK_1000" },
};

export function isTopupPackId(v: unknown): v is TopupPackId {
  return typeof v === "string" && v in TOPUP_PACKS;
}

// Base list price for an off-pack credit top-up, in cents per credit. This
// is the standard (smallest-pack) rate — pack_100 is $15 / 1000 = $0.015 —
// before any volume or admin discount. Used by the admin "add balance"
// flow, which charges the user's saved card for `credits` at this rate
// minus an admin-entered discount. Fractional (1.5¢): every cents total
// derived from it is rounded to a whole cent.
export const TOPUP_BASE_CENTS_PER_CREDIT = 1.5;

// Plan multiplier applied to every top-up price, mirroring the subscription
// tier multipliers (lib/pricing/credit-tiers.ts): "each tier 2× the one below
// for the same credits." Pro is the ×1 base; Grow ×2; Scale ×4. Free, the
// legacy "starter" alias, and any unknown/absent plan fall back to the ×1 base
// so top-ups on those plans are unchanged. Callers that omit `plan` therefore
// get the original flat price — the multiplier is strictly opt-in per caller.
export function topupPlanMultiplier(plan: PlanId | null | undefined): number {
  switch (plan) {
    case "grow":
      return 2;
    case "scale":
      return 4;
    default:
      return 1; // free, starter, pro, or unknown → base rate
  }
}

// Every top-up charge is rounded UP to a whole dollar — a top-up is never a
// fractional-dollar amount (e.g. $4.50 → $5, $77.30 → $78). Applied AFTER the
// plan multiplier so each tier's own price is what gets rounded.
export function ceilToDollarCents(cents: number): number {
  return Math.ceil(cents / 100) * 100;
}

// Slider-based top-up range (unified credits modal). The slider lets a
// user pick any whole-number credit amount in this range; price is
// `round(credits × TOPUP_BASE_CENTS_PER_CREDIT × plan multiplier)`. Min is 650
// (the base credit tier) — the cheapest purchase (650 × 1.5¢ = $9.75, more on
// higher plans) clears Stripe's $0.50 minimum with plenty of headroom.
export const TOPUP_MIN_CREDITS = 650;
export const TOPUP_MAX_CREDITS = 20000;
export const TOPUP_STEP_CREDITS = 100;

// Auto-refill (threshold-based recurring top-up) is governed separately
// from the manual slider — the spec's largest refill option (5,000
// credits) exceeds the slider max because a refill is meant to last a
// while, whereas the slider is shaped for one-off pick-me-ups.
export const AUTO_REFILL_MIN_CREDITS = 1000;
export const AUTO_REFILL_MAX_CREDITS = 50000;

export function clampTopupCredits(n: number): number {
  if (!Number.isFinite(n)) return TOPUP_MIN_CREDITS;
  const rounded = Math.round(n);
  if (rounded < TOPUP_MIN_CREDITS) return TOPUP_MIN_CREDITS;
  if (rounded > TOPUP_MAX_CREDITS) return TOPUP_MAX_CREDITS;
  return rounded;
}

// Server-side price computation for an arbitrary slider amount. Always
// uses the base list rate × the plan multiplier — never trust a client-supplied
// price. `plan` omitted ⇒ ×1 (base rate), preserving legacy behavior.
export function computeTopupCents(
  credits: number,
  plan?: PlanId | null,
): number {
  return ceilToDollarCents(
    clampTopupCredits(credits) *
      TOPUP_BASE_CENTS_PER_CREDIT *
      topupPlanMultiplier(plan),
  );
}

// Webhook validator for slider top-ups. Returns the credit award only
// if the Stripe-reported amount matches what we expect for the claimed
// credit count. Any mismatch (or out-of-range credits) = null; caller
// MUST log + skip rather than fall through to a default grant.
export function deriveArbitraryTopupGrant(
  credits: number | null | undefined,
  amountTotalCents: number | null | undefined,
  plan?: PlanId | null,
): { credits: number; priceCents: number } | null {
  if (typeof credits !== "number" || !Number.isInteger(credits)) return null;
  if (credits < TOPUP_MIN_CREDITS || credits > TOPUP_MAX_CREDITS) return null;
  const expected = ceilToDollarCents(
    credits * TOPUP_BASE_CENTS_PER_CREDIT * topupPlanMultiplier(plan),
  );
  if (typeof amountTotalCents !== "number" || amountTotalCents !== expected) {
    return null;
  }
  return { credits, priceCents: expected };
}

/**
 * Minimum chargeable amount, in USD cents. A discounted top-up must clear it
 * to be billable.
 *
 * PINNED TO USD, and only correct because every capture in this codebase
 * settles in USD — Paddle prices carry a single USD unit_price with no
 * unit_price_overrides, no extra currencies are enabled, and
 * verifyCaptureCurrency rejects anything else before an amount is compared.
 * The floor itself is processor-agnostic ($0.50 is the common card minimum),
 * so the old Stripe-specific name was misleading rather than the value.
 *
 * If local-currency pricing is ever enabled, this becomes per-currency and
 * every call site needs the presentment currency alongside the amount.
 */
export const MIN_CHARGE_CENTS_USD = 50;

/** @deprecated Use MIN_CHARGE_CENTS_USD — the floor is not Stripe-specific. */
export const STRIPE_MIN_CHARGE_CENTS = MIN_CHARGE_CENTS_USD;

// Compute the base + discounted charge (in cents) for an admin top-up of
// `credits` at `discountPercent` off. Discount is clamped to 0–100.
export function computeAdminTopupPrice(
  credits: number,
  discountPercent: number,
  plan?: PlanId | null,
): { baseCents: number; chargeCents: number } {
  const baseCents = ceilToDollarCents(
    credits * TOPUP_BASE_CENTS_PER_CREDIT * topupPlanMultiplier(plan),
  );
  const discount = Math.min(100, Math.max(0, discountPercent));
  const chargeCents = Math.round(baseCents * (1 - discount / 100));
  return { baseCents, chargeCents };
}

export function topupPackFromPriceId(priceId: string | null | undefined): TopupPackId | null {
  if (!priceId) return null;
  for (const pack of Object.values(TOPUP_PACKS)) {
    if (process.env[pack.priceIdEnv] === priceId) return pack.id;
  }
  return null;
}

// Resolve a pack by ID with its env-derived Stripe price ID. Used by the
// top-up checkout route to mint a Stripe session referencing the right price.
export function getTopupPack(
  packId: string | null | undefined,
  plan?: PlanId | null,
): { id: TopupPackId; credits: number; priceCents: number; priceId: string | undefined } | null {
  if (!isTopupPackId(packId)) return null;
  const pack = TOPUP_PACKS[packId];
  const mult = topupPlanMultiplier(plan);
  return {
    id: pack.id,
    credits: pack.credits,
    priceCents: pack.priceCents * mult,
    // The pre-created Stripe Price (STRIPE_PRICE_PACK_*) encodes the ×1 base
    // amount, so it can only be reused at the base multiplier. For Grow (×2) /
    // Scale (×4) we charge the scaled amount via inline price_data — return no
    // priceId so the checkout route falls back to inline pricing.
    priceId: mult === 1 ? process.env[pack.priceIdEnv] : undefined,
  };
}

// Webhook validator: returns the credit award only if the Stripe-reported
// amount matches what we expect for the claimed pack. Any mismatch = null;
// caller MUST log + skip rather than fall through to a default grant.
export function deriveTopupGrant(
  packId: string | null | undefined,
  amountTotalCents: number | null | undefined,
  plan?: PlanId | null,
): { credits: number; priceCents: number } | null {
  if (!isTopupPackId(packId)) return null;
  const pack = TOPUP_PACKS[packId];
  const expected = pack.priceCents * topupPlanMultiplier(plan);
  if (typeof amountTotalCents !== "number" || amountTotalCents !== expected) {
    return null;
  }
  return { credits: pack.credits, priceCents: expected };
}

// ── Marketing surface (pricing page comparison table) ─────────────────
// One row in the "Compare all features" table. Each `values` tuple is
// [free, starter, pro, scale] OR [starter, pro, scale] depending on
// whether the pricing page renders the free column. Default is the
// 3-paid layout matching the screenshot.

export interface ComparisonRow {
  label: string;
  values: [string | boolean, string | boolean, string | boolean];
}

export const COMPARISON_SECTIONS: Array<{ heading: string; rows: ComparisonRow[] }> = [
  {
    heading: "Build",
    rows: [
      { label: "AI build capacity", values: ["300 credits/month", "1,000 credits/month", "3,000 credits/month"] },
      { label: "Projects",          values: ["5",                 "10",                 "50"] },
      { label: "Custom domain",     values: [false,               true,                 true] },
      { label: "Accept payments",   values: [false,               true,                 true] },
      { label: "Remove Panda branding", values: [false,           true,                 true] },
    ],
  },
  {
    heading: "Grow",
    rows: [
      { label: "Autonomous Grow Mode",                       values: [true, true, true] },
      { label: "Meta ads, email, WhatsApp, social",          values: [true, true, true] },
      { label: "AI Meta ad creative (static + video)",       values: [true, true, true] },
      { label: "Automated follow-ups",                       values: [true, true, true] },
    ],
  },
  {
    heading: "Support",
    rows: [
      { label: "Community support", values: [true,  true,  true] },
      { label: "Priority support",  values: [false, false, true] },
    ],
  },
];

export const TRY_FREE_FEATURES = [
  "300 free credits (first month) — 50/day",
  "3 projects",
  "Hosting included",
  "Premium templates",
  "Community publishing",
  // Same backend boundary the Free pricing card states (PANDA-1676/1695) —
  // this list is a second free-tier surface, so it must not read as a fuller
  // offer than the card. See PRICING_TIERS.free in lib/pricing/credit-tiers.ts.
  "Database & customer login as a demo (data resets)",
];

export const CONTACT_SALES_MAILTO =
  "mailto:sales@panda.new?subject=Panda%20Enterprise%20Inquiry&body=" +
  encodeURIComponent(
    [
      "Hi Panda team,",
      "",
      "I'd like to learn more about Panda for my team. A few quick details:",
      "",
      "• Company:",
      "• Team size:",
      "• What you're looking to do with Panda (custom volumes, onboarding, SSO, dedicated support, etc.):",
      "• Timeline:",
      "",
      "Thanks!",
    ].join("\n"),
  );
